import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col, Container, Button, Label, Input } from "reactstrap";
import { getOrder, getVidRequest } from "~/helpers/backendHelper";
import iconSrc from '~/assets/images/desktop/additional-info-icon.png';
import progressSrc from '~/assets/images/desktop/progress-bar-complete.png';
import DesktopMenu from "~/components/Shared/DesktopMenu";
import { showError, showBriefSuccess } from "~/helpers/utilHelper";
import { uploadSecondForOfId } from "~/helpers/backendHelper";
import { useNavigator } from "~/context/navigator";
import menuSteps from "~/constants/menuSteps";
import additionalImage from "~/assets/images/desktop/additional-info-banner.png";
import addImg from "~/assets/images/desktop/add-img.svg";
import { Link, useNavigate } from "react-router-dom";
import vidTokenStatus from "~/constants/vidTokenStatus";
import Order from "~/model/order";
import { getBeUrl } from "~/helpers/utilHelper";
import Preloader from "~/components/Shared/Preloader";

const DesktopContent = () => {

  // navigator context that helps determine the next available route
  const { getNextRoute } = useNavigator();
  // router hook that helps redirect
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [_orderError, setOrderError] = useState(false);
  const [isOrderInProgress, setIsOrderInProgress] = useState(false);
  const [isVidRequestInProgress, setIsVidRequestInProgress] = useState(false);
  const [isSecondFormRequired, setIsSecondFormRequired] = useState<boolean | null>(null);

  const [secondFormImageFile, setSecondFormImageFile] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (order?.secondFormOfId) {
      setSecondFormImageFile(order.secondFormOfId);
    }
  }, [order]);

  useEffect(() => {
    if (!!isSecondFormRequired) {
      setIsOrderInProgress(true);
      getOrder().then(res => {
        setOrder(res.order)
      }).catch(_err => {
        setOrderError(true);
      }).finally(() => {
        setIsOrderInProgress(false);
      })
    }
  }, [isSecondFormRequired]);

  // runs once on component mount
  useEffect(() => {
    setIsVidRequestInProgress(true);
    getVidRequest().then(res => {
      setIsSecondFormRequired(res.vidRequest.status != vidTokenStatus.SKIPPED)
    }).finally(() => {
      setIsVidRequestInProgress(false);
    })
  }, []);

  // Function to determine if the file is a PDF doc
  const isPdfFile = (file: File | { type: string } | string) => {
    if (!file) return false;
    if (file instanceof File) {
      return file.type === 'application/pdf';
    }
    if (typeof file === 'object' && file.type) {
      return file.type === 'application/pdf';
    }
    if (typeof file === 'string') {
      return /\.pdf$/i.test(file);
    }
    return false;
  };

  // Function to determine if the file is an image
  const isImageFile = (file: File | { type: string } | string) => {
    if (!file) return false;
    if (file instanceof File) {
      return file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
    }
    if (typeof file === 'object' && file.type) {
      return file.type.startsWith('image/');
    }
    if (typeof file === 'string') {
      return /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(file);
    }
    return false;
  };

  // Function to get image URL for preview
  const getImagePreviewUrl = (file: File | { name: string } | string) => {
    if (file instanceof File) {
      return URL.createObjectURL(file);
    }
    if (typeof file === 'object' && file.name) {
      return getBeUrl(`order/${order?.id}/second-id/render`);
    }
    if (typeof file === 'string') {
      return getBeUrl(`order/${order?.id}/second-id/render`);
    }

    return null;
  };

  // Function to get file name for display
  const getFileName = (file: File | { name: string } | string) => {
    if (!file) return '';
    if (file instanceof File) {
      return file.name;
    }
    if (typeof file === 'object' && file.name) {
      return file.name;
    }
    if (typeof file === 'string') {
      return file;
    }
    return 'Document';
  };

  const handleSecondFormImageUpload = (file: File | { type: string } | string) => {
    if (file && (file instanceof File && (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'application/pdf'))) {
      setSecondFormImageFile(file);
    }
  };

  const handleDeleteSecondFormImage = () => {
    setSecondFormImageFile(null);
  };

  const handleImageUploadClick = () => {
    const input = document.getElementById('secondFormImageUpload');
    if (input) {
      input.click();
    }
  };

  const handleContinue = async () => {
    const fileToUpload = secondFormImageFile;

    if (!fileToUpload) {
      return;
    }

    // Check if file is from backend (not a new File object)
    if (!(fileToUpload instanceof File)) {
      // File already exists in backend, just proceed
      showBriefSuccess('Document has been sent');
      navigate(getNextRoute());
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = async function (e) {

      try {
        const base64File = {
          name: fileToUpload.name,
          type: fileToUpload.type,
          size: fileToUpload.size,
          data: e.target?.result
        };

        const payload = {
          secondFormOfId: base64File
        };

        await uploadSecondForOfId(payload);
        showBriefSuccess('Document has been sent');
        navigate(getNextRoute());

      } catch (error) {
        console.error("Upload failed:", error);
        showError("Unable to send document");
      } finally {
        setIsUploading(false);
      }
    };

    reader.onerror = function (_error) {
      setIsUploading(false);
      showError("Unable to read file");
    };
    reader.readAsDataURL(fileToUpload);
  };

  return <React.Fragment>
    <DesktopMenu title={"Additional Information"} activeStep={menuSteps.ADDITIONALINFORMATION} />
    <Container>
      <div className="d-flex justify-content-center" id="additional-desktop-header">
        <img className="header-image-finish-desktop" src={additionalImage} id="header_image" />
      </div>
      <Row className="justify-content-center">
        <Col md={8} lg={6} xl={6} className="finish-column desktop">
          <Card className="mb-2">
            <CardBody>
              {isSecondFormRequired && <div className="pt-1 text-center">
                <img className="mt-2 verif-icon" src={iconSrc}></img>
                <div className="additional-content-parent">
                  <h5 className="mt-4 mb-4 finish-name"> To proceed, please provide a valid 2nd form of ID for verification purposes.</h5>
                  <img src={progressSrc} className="mb-0"></img>
                  <h6 className="mt-4 mb-4 finish-name">Upload a valid second form of ID</h6>
                  <p className="finish-message-desktop mb-2">This can be a US Passport, Vehicle Insurance Card, Vehicle Registration, Utility Bill, Mortgage or Renters Agreement, etc. *Please note that Credit Cards and Debit Cards are not accepted.</p>
                </div>
                <div className="upload-area mt-2">
                  <Row className="mt-4 mb-4 d-flex justify-content-center">
                    {/* Image/PDF Upload Column */}
                    <Col xs={6} className="pe-2">
                      <div className="upload-section-container">
                        {secondFormImageFile ? (
                          <div className="upload-preview-container">
                            <div className="upload-preview">
                              {isImageFile(secondFormImageFile) ? (
                                // Preview for image
                                <img
                                  src={getImagePreviewUrl(secondFormImageFile) as string}
                                  alt="Second form of id receipt"
                                  className="preview-image w-100"
                                  style={{ maxHeight: '150px', objectFit: 'cover' }}
                                />
                              ) : isPdfFile(secondFormImageFile) ? (
                                // Preview for PDF
                                <div className="pdf-preview-container d-flex justify-content-center align-items-center" style={{ height: '150px', backgroundColor: '#f8f9fa' }}>
                                  <div className="text-center">
                                    <i className="mdi mdi-file-pdf-box" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                                    <p className="mt-2 mb-0 text-muted small">{getFileName(secondFormImageFile)}</p>
                                  </div>
                                </div>
                              ) : null}
                              {
                                secondFormImageFile instanceof File &&
                                <Button
                                  color="danger"
                                  size="sm"
                                  className="delete-upload-btn position-absolute top-0 end-0 m-1"
                                  onClick={handleDeleteSecondFormImage}
                                >
                                  <i className="mdi mdi-delete" />
                                </Button>
                              }
                            </div>
                          </div>
                        ) : (
                          <div className="upload-placeholder-container">
                            <Label htmlFor="secondFormImageUpload" className="upload-placeholder-label w-100" style={{ cursor: 'pointer' }}>
                              <div className="upload-placeholder-content text-center p-4 border border-dashed">
                                <img className="me-2" src={addImg} alt="Upload" style={{ cursor: 'pointer' }} />
                              </div>
                            </Label>
                            <Input
                              type="file"
                              id="secondFormImageUpload"
                              accept="image/jpeg,image/png,application/pdf"
                              style={{ display: 'none' }}
                              onChange={(e) => handleSecondFormImageUpload(e.target.files![0])}
                            />
                          </div>
                        )}
                        {
                          !secondFormImageFile &&
                          <Button
                            outline
                            color="primary"
                            className="upload-action-btn w-100 mt-2  btn btn-primary text-white"
                            onClick={handleImageUploadClick}
                          >
                            Upload Image Or PDF
                          </Button>
                        }
                      </div>
                    </Col>
                  </Row>
                </div>
                <div className="continue-area justify-content-center d-flex">
                  <Button
                    type="button"
                    color="primary"
                    className="mt-5 mb-2 btn-additional-screen"
                    onClick={handleContinue}
                    disabled={isUploading || !secondFormImageFile}
                  >
                    {isUploading ? "Uploading..." : "Continue"}
                  </Button>
                </div>
              </div>}
              {!isSecondFormRequired && !!order &&
                <div className="pt-1 text-center">
                  <img className="mt-2 verif-icon" src={iconSrc}></img>
                  <div className="font-size-16 mt-5 font-weight-semibold">
                    Identity Verification and Second Form if ID requirements have been waived.
                  </div>
                  <img src={progressSrc} className="mt-3 mb-0"></img>
                  <div className="font-size-15 mt-4 mb-4">
                    Click &quot;Continue&quot; to proceed with the signing.
                  </div>
                  <div className="continue-area justify-content-center d-flex">
                    <Link id="continueBtn" className="btn mt-4 mb-4 btn-primary btn-additional-screen-mobile" to={getNextRoute()}>Continue</Link>
                  </div>
                </div>}
              {(isOrderInProgress || isVidRequestInProgress) && <Preloader />}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  </React.Fragment>
}

export default DesktopContent;
